import React, { useState, useEffect } from 'react';
import { FileText, GripVertical, Eye, EyeOff, Save, RotateCcw, Loader2, RefreshCw, Plus, Trash2, Edit2, X, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetClientDocumentsConfig = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingName, setEditingName] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/config/client-documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/config/client-documents`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ documents })
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser la configuration ? Tous les documents seront visibles.')) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/config/client-documents/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setDocuments(data.config.documents || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur reset:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/config/client-documents/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setDocuments(data.config.documents || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  const toggleVisibility = (index) => {
    const newDocs = [...documents];
    newDocs[index].visible = !newDocs[index].visible;
    setDocuments(newDocs);
    setHasChanges(true);
  };

  // Ajouter un nouveau document
  const handleAddDocument = () => {
    if (!newDocName.trim()) return;
    
    const code = newDocName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    
    const newDoc = {
      code: code + '_' + Date.now(),
      nom: newDocName.trim(),
      visible: true,
      ordre: documents.length
    };
    
    setDocuments([...documents, newDoc]);
    setNewDocName('');
    setShowAddModal(false);
    setHasChanges(true);
  };

  // Supprimer un document
  const handleDeleteDocument = (index) => {
    if (!window.confirm('Supprimer ce document de la liste ?')) return;
    
    const newDocs = documents.filter((_, i) => i !== index);
    newDocs.forEach((doc, idx) => {
      doc.ordre = idx;
    });
    setDocuments(newDocs);
    setHasChanges(true);
  };

  // Commencer l'édition
  const startEditing = (index) => {
    setEditingDoc(index);
    setEditingName(documents[index].nom);
  };

  // Sauvegarder l'édition
  const saveEditing = () => {
    if (!editingName.trim()) return;
    
    const newDocs = [...documents];
    newDocs[editingDoc].nom = editingName.trim();
    setDocuments(newDocs);
    setEditingDoc(null);
    setEditingName('');
    setHasChanges(true);
  };

  // Annuler l'édition
  const cancelEditing = () => {
    setEditingDoc(null);
    setEditingName('');
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newDocs = [...documents];
    const draggedItem = newDocs[draggedIndex];
    newDocs.splice(draggedIndex, 1);
    newDocs.splice(index, 0, draggedItem);
    
    // Mettre à jour les ordres
    newDocs.forEach((doc, idx) => {
      doc.ordre = idx;
    });

    setDocuments(newDocs);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const visibleCount = documents.filter(d => d.visible).length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-violet-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Lien client</h3>
              <p className="text-purple-100 text-sm">Documents visibles pour le client</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm">
              {visibleCount}/{documents.length} visibles
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-gray-600 mb-4">
          Configurez les documents que le client peut voir et uploader via le lien partagé. 
          Glissez-déposez pour réordonner.
        </p>

        {/* Liste des documents */}
        <div className="space-y-2 mb-4">
          {documents.map((doc, index) => (
            <div
              key={doc.code}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition cursor-move ${
                draggedIndex === index
                  ? 'border-purple-400 bg-purple-50 shadow-lg'
                  : doc.visible
                    ? 'border-green-200 bg-green-50 hover:border-green-300'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              {/* Handle drag */}
              <div className="text-gray-400 hover:text-gray-600">
                <GripVertical className="w-5 h-5" />
              </div>

              {/* Numéro d'ordre */}
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                doc.visible ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {index + 1}
              </div>

              {/* Nom du document */}
              <div className="flex-1">
                {editingDoc === index ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-2 py-1 border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEditing();
                        if (e.key === 'Escape') cancelEditing();
                      }}
                    />
                    <button
                      onClick={saveEditing}
                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className={`font-medium ${doc.visible ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                    {doc.nom}
                  </span>
                )}
              </div>

              {/* Bouton éditer */}
              {editingDoc !== index && (
                <button
                  onClick={() => startEditing(index)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  title="Modifier le nom"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}

              {/* Toggle visibilité */}
              <button
                onClick={() => toggleVisibility(index)}
                className={`p-2 rounded-lg transition ${
                  doc.visible
                    ? 'bg-green-100 text-green-600 hover:bg-green-200'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
                title={doc.visible ? 'Masquer pour le client' : 'Afficher pour le client'}
              >
                {doc.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>

              {/* Bouton supprimer */}
              <button
                onClick={() => handleDeleteDocument(index)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                title="Supprimer ce document"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Bouton ajouter */}
        <button
          onClick={() => setShowAddModal(true)}
          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50 hover:border-purple-400 transition mb-4"
        >
          <Plus className="w-5 h-5" />
          Ajouter un document
        </button>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
              hasChanges
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer
              </>
            )}
          </button>
          
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition disabled:opacity-50"
            title="Synchroniser avec les nouveaux types de documents"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={handleReset}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition disabled:opacity-50"
            title="Réinitialiser"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        {hasChanges && (
          <p className="text-sm text-orange-600 mt-2 text-center">
            ⚠️ Modifications non enregistrées
          </p>
        )}
      </div>

      {/* Modal Ajouter un document */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-violet-600 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Ajouter un document
              </h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du document
              </label>
              <input
                type="text"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="Ex: Attestation de domicile"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddDocument();
                  if (e.key === 'Escape') {
                    setShowAddModal(false);
                    setNewDocName('');
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-2">
                Ce document sera visible par le client dans le lien de dépôt des documents.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewDocName('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleAddDocument}
                disabled={!newDocName.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetClientDocumentsConfig;
